import { useMemo, useState } from "react";
import { useSevenAccess, SevenShell } from "./DashboardSeven";
import { CARD_CATEGORIES, ACCESS } from "../../data/sevenNights";
import { Shuffle, Copy, Check } from "lucide-react";
import { FONT_DISPLAY } from "../../lib/sevenTheme";

export default function CardsSeven() {
  const { loading, bundle } = useSevenAccess();
  const [active, setActive] = useState("all");
  const [picked, setPicked] = useState(null);
  const [copied, setCopied] = useState(null);
  const allowedCount = (ACCESS[bundle] || ACCESS.starter).cards;

  // Each bundle gets its allotted count, distributed proportionally across categories.
  // Compute BEFORE any early return — hooks must always run.
  const visibleCards = useMemo(() => {
    let cards = [];
    CARD_CATEGORIES.forEach((cat) => {
      const share = Math.ceil((cat.items.length / 150) * allowedCount);
      cat.items.slice(0, share).forEach((q) => cards.push({ q, cat: cat.key, catLabel: cat.label }));
    });
    return cards.slice(0, allowedCount);
  }, [allowedCount]);

  if (loading) return <div className="min-h-screen bg-[#0A0606] flex items-center justify-center text-[#A7956E]">جاري التحميل…</div>;

  const filtered = active === "all" ? visibleCards : visibleCards.filter((c) => c.cat === active);

  const pickRandom = () => {
    if (filtered.length === 0) return;
    const r = filtered[Math.floor(Math.random() * filtered.length)];
    setPicked(r);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const copy = async (text, i) => {
    try { await navigator.clipboard.writeText(text); setCopied(i); setTimeout(() => setCopied(null), 1400); } catch (_) { /* clipboard blocked */ }
  };

  return (
    <SevenShell title="مكتبة البطاقات" testid="seven-cards-root">
      <p className="text-[14px] text-[#CFC3A1] leading-[1.95] mb-6 max-w-2xl">
        لديكِ وصول إلى <span className="text-[#C9A66A] font-bold">{allowedCount} بطاقة</span>. اختاري فئة، أو اضغطي «بطاقة عشوائية» للحظة قرب سريعة.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center mb-6">
        <button onClick={pickRandom} data-testid="seven-card-random" className="inline-flex items-center justify-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] hover:from-[#EBCD93] hover:to-[#D6B477] text-[#0A0606] font-extrabold text-[13.5px] tracking-wide rounded-full px-6 py-3 min-h-[46px]">
          <Shuffle className="w-4 h-4" /> اسحبي بطاقة عشوائية
        </button>
        {picked && (
          <div className="flex-1 bg-gradient-to-b from-[#2A1414] to-[#150C0C] border border-[#C9A66A]/40 rounded-xl px-5 py-3.5 text-[14px] text-white" data-testid="seven-card-picked">
            <div className="text-[10px] tracking-[0.24em] text-[#C9A66A] font-bold mb-1">{picked.catLabel}</div>
            {picked.q}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="seven-cards-filters">
        <button onClick={() => setActive("all")} className={`text-[12px] font-bold tracking-wide px-4 py-2 rounded-full border transition-all ${active === "all" ? "bg-[#C9A66A]/15 border-[#C9A66A]/55 text-[#E7C98C]" : "border-white/[0.10] text-[#CFC3A1] hover:border-[#C9A66A]/35"}`}>الكل ({visibleCards.length})</button>
        {CARD_CATEGORIES.map((cat) => {
          const count = visibleCards.filter((c) => c.cat === cat.key).length;
          if (count === 0) return null;
          return (
            <button key={cat.key} onClick={() => setActive(cat.key)} data-testid={`seven-cards-filter-${cat.key}`} className={`text-[12px] font-bold tracking-wide px-4 py-2 rounded-full border transition-all ${active === cat.key ? "bg-[#C9A66A]/15 border-[#C9A66A]/55 text-[#E7C98C]" : "border-white/[0.10] text-[#CFC3A1] hover:border-[#C9A66A]/35"}`}>
              {cat.label} ({count})
            </button>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-2 gap-3" data-testid="seven-cards-grid">
        {filtered.map((c, i) => (
          <div key={i} className="group relative bg-[#1C1010]/60 border border-[#C9A66A]/18 hover:border-[#C9A66A]/45 rounded-xl px-5 py-4 transition-all">
            <div className="text-[9.5px] tracking-[0.24em] text-[#A7956E] font-bold mb-1.5">{c.catLabel}</div>
            <div className="text-[14px] text-[#F5EAD3] leading-[1.85] pr-7">{c.q}</div>
            <button onClick={() => copy(c.q, i)} className="absolute top-3 left-3 opacity-60 hover:opacity-100 text-[#C9A66A] transition-opacity">
              {copied === i ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        ))}
      </div>
    </SevenShell>
  );
}
